import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { PUBLIC_SITE_URL, DEFAULT_OG_IMAGE } from "../../config/site";
import { getCorridorPageSeo } from "../../config/corridorSeoMeta";
import { PAGE_SEO } from "../../config/pageSeo";

const DEFAULT_TITLE = "Duri Ridesharing App | Intercity, Intracity & Daily Commute Carpool";
const DEFAULT_DESCRIPTION =
  "Trusted ridesharing app for intercity travel, intracity trips, and daily commute carpool. Find rides, offer seats, and travel with verified riders and hosts.";

const ROBOTS_INDEX = "index, follow, max-image-preview:large, max-snippet:-1";
const ROBOTS_NOINDEX = "noindex, nofollow";

/** Private or low-value routes — must not be indexed. */
const NOINDEX_PATH_PREFIXES = [
  "/dashboard",
  "/account",
  "/delete-account",
  "/webview-init",
  "/dev-team",
  "/login",
  "/payment",
  "/admin",
];

/** Corridor pages set title/OG in CorridorPageView — sync canonical, robots, OG image only. */
const CORRIDOR_PATH_PREFIX = "/routes/";

function upsertMeta(name: string, content: string) {
  let el = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertOg(property: string, content: string) {
  let el = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("property", property);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertCanonical(href: string) {
  let el = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function shouldNoindex(pathNorm: string): boolean {
  return NOINDEX_PATH_PREFIXES.some((p) => pathNorm === p || pathNorm.startsWith(`${p}/`));
}

function isCorridorPage(pathNorm: string): boolean {
  return pathNorm.startsWith(CORRIDOR_PATH_PREFIX) && pathNorm.length > CORRIDOR_PATH_PREFIX.length;
}

function resolvePageMeta(pathNorm: string): { title: string; description: string } | undefined {
  if (PAGE_SEO[pathNorm]) return PAGE_SEO[pathNorm];

  const corridorMatch = pathNorm.match(/^\/routes\/([^/]+)$/);
  if (corridorMatch) {
    return getCorridorPageSeo(corridorMatch[1]);
  }

  return undefined;
}

function applyMeta(
  meta: { title: string; description: string },
  canonical: string,
  ogImage: string,
  options?: { isArticle?: boolean; publishedTime?: string; author?: string; keywords?: string }
) {
  document.title = meta.title;
  upsertMeta("description", meta.description);
  upsertMeta("robots", ROBOTS_INDEX);
  upsertOg("og:type", options?.isArticle ? "article" : "website");
  upsertOg("og:site_name", "Duri Ridesharing");
  upsertOg("og:title", meta.title);
  upsertOg("og:description", meta.description);
  upsertOg("og:url", canonical);
  upsertOg("og:image", ogImage);
  upsertOg("og:locale", "en_IN");
  upsertMeta("twitter:card", "summary_large_image");
  upsertMeta("twitter:title", meta.title);
  upsertMeta("twitter:description", meta.description);
  upsertMeta("twitter:image", ogImage);

  if (options?.keywords) {
    upsertMeta("keywords", options.keywords);
  }
  if (options?.publishedTime) {
    upsertOg("article:published_time", options.publishedTime);
  }
  if (options?.author) {
    upsertOg("article:author", options.author);
  }
}

/**
 * Syncs canonical, robots, title, description, and Open Graph for marketing routes.
 * Canonical always uses the clean path (no ?utm_ or ?gclid= query strings).
 */
export function SeoRouteSync() {
  const { pathname } = useLocation();

  useEffect(() => {
    const pathNorm = pathname.replace(/\/$/, "") || "/";
    const canonical = `${PUBLIC_SITE_URL}${pathNorm === "/" ? "/" : pathNorm}`;

    upsertCanonical(canonical);

    if (shouldNoindex(pathNorm)) {
      upsertMeta("robots", ROBOTS_NOINDEX);
      return;
    }

    if (isCorridorPage(pathNorm)) {
      upsertMeta("robots", ROBOTS_INDEX);
      upsertOg("og:url", canonical);
      upsertOg("og:image", DEFAULT_OG_IMAGE);
      return;
    }

    // —— Special Article/News Route: rich thumbnail, keywords, and article schema ——
    if (pathNorm === "/news") {
      const newsMeta = resolvePageMeta(pathNorm) ?? {
        title: "Mohan Nanduri Keynote at KR Bharat Conclave 2026 | Duri News",
        description:
          "Watch Mohan Nanduri's keynote address at KR Bharat Conclave 2026 on redefining Indian mobility, live Vahan & DigiLocker trust, and sustainable carpooling under Vikasit Bharat 2047.",
      };
      applyMeta(
        newsMeta,
        canonical,
        `${PUBLIC_SITE_URL}/images/kr-bharat-conclave-2026.webp`,
        {
          isArticle: true,
          publishedTime: "2026-07-26T10:00:00+05:30",
          author: "Mohan Nanduri",
          keywords:
            "Mohan Nanduri, KR Bharat Conclave 2026, Duri App, ridesharing India, carpool Hyderabad, Vikasit Bharat 2047, Vahan verification, DigiLocker, T-Hub",
        }
      );
      return;
    }

    const meta = resolvePageMeta(pathNorm);
    if (meta) {
      applyMeta(meta, canonical, DEFAULT_OG_IMAGE);
      return;
    }

    applyMeta({ title: DEFAULT_TITLE, description: DEFAULT_DESCRIPTION }, canonical, DEFAULT_OG_IMAGE);
  }, [pathname]);

  return null;
}
